import cn from 'classnames'
import React from 'react'
import { wrapTextChildrenWithTypography } from '../../utils/wrapChildrenWithTypography'
import { Icon } from '../icon'
import styles from './Button.module.css'
import { ButtonComponent, ButtonProps, ButtonSize, ButtonVariant } from './Button.types'
import { Icon as ButtonIcon } from './Icon'

const SIZE_CLASS: Record<ButtonSize, string> = {
  M: styles.sizeM,
  S: styles.sizeS,
  XS: styles.sizeXS,
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: styles.variantPrimary,
  secondary: styles.variantSecondary,
  transparentPrimary: styles.variantTransparentPrimary,
  transparentNeutral: styles.variantTransparentNeutral,
  outlinedPrimary: styles.variantOutlinedPrimary,
  outlinedNeutral: styles.variantOutlinedNeutral,
  overlayPrimary: styles.variantOverlayPrimary,
  overlayNeutral: styles.variantOverlayNeutral,
}

function ButtonBase({
  ref,
  size: sizeProp,
  variant: variantProp,
  fullWidth = false,
  isLoading = false,
  className,
  children,
  as,
  type,
  ariaLabel,
  ...rest
}: ButtonProps<React.ElementType>) {
  const Component: React.ElementType = as || 'button'
  const size: ButtonSize = sizeProp ?? 'M'
  const variant: ButtonVariant = variantProp ?? 'primary'
  const labelClassName = cn(
    styles.label,
    'uds-button-label',
  )

  const wrappedChildren = wrapTextChildrenWithTypography(children, labelClassName)

  const resolvedType = Component === 'button' ? (type ?? 'button') : type

  return (
    <Component
      {...(rest as any)}
      ref={ref}
      type={resolvedType}
      className={cn(
        styles.button,
        SIZE_CLASS[size],
        VARIANT_CLASS[variant],
        fullWidth && styles.fullWidth,
        isLoading && styles.contentHidden,
        className,
      )}
      aria-label={ariaLabel}
    >
      {isLoading && <Icon glyph="Spinner" className={cn(styles.loadingIcon, 'uds-button-loading-icon')} />}
      {wrappedChildren}
    </Component>
  )
}

ButtonBase.displayName = 'Button'

const Button: ButtonComponent = Object.assign(
  ButtonBase,
  {
    Icon: ButtonIcon,
  },
)

export default Button
